<template>
  <div>
    <EquipmentEditHeader v-if="isAdmin" data-cy="equipment-edit-header" />
    <div v-else class="mb-3">
      <div class="flex align-items-center gap-4">
        <div v-if="isHps" class="flex align-items-center field-checkbox">
          <Checkbox
            v-model="enableFilter"
            input-id="enable-hps-intake-filter"
            binary
          />
          <label for="enable-hps-intake-filter">Filter and Sort Suction</label>
        </div>
      </div>
    </div>

    <div
      v-if="showPumpRequiredWarning"
      class="no-recommendation-message-container"
      data-cy="hps-intake-pump-required-warning"
    >
      <div class="no-recommendation-message">
        <p class="no-recommendation-heading">
          Select a pump first to get suction recommendations.
        </p>
        <p class="no-recommendation-body">
          To get reccomendations please choose a pump first
        </p>
        <p class="no-recommendation-body mt-2">
          To browse all suctions without reccomendations, un-check
          <span class="no-recommendation-highlight"
            >&quot;Filter and Sort Suction&quot;</span
          >.
        </p>
      </div>
    </div>
    <div
      v-else-if="showNoMatchingSuctionsWarning"
      class="no-recommendation-message-container"
      data-cy="hps-intake-no-matches-warning"
    >
      <div class="no-recommendation-message">
        <p class="no-recommendation-heading">
          No suctions match this configuration.
        </p>
        <p class="no-recommendation-body">
          If you&apos;d like to select a suction, please un-check
          <span class="no-recommendation-highlight"
            >&quot;Filter and Sort Suction&quot;</span
          >.
        </p>
      </div>
    </div>

    <EquipmentTable
      v-else
      data-cy="equipment-table"
      @select="$emit('select', $event.data as Intakes)"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, provide, ref } from "vue";
import Checkbox from "primevue/checkbox";
import { useHpsIntakes } from "@/composables/equipment/hpsIntakes/hpsIntakes";
import EquipmentEditHeader from "@/components/equipment/EquipmentEditHeader.vue";
import EquipmentTable from "@/components/equipment/EquipmentTable.vue";
import type { Intakes } from "gql/api";
import { injectStrict } from "@/utils/utils";
import {
  scenarioCalculationsKey,
  scenarioFormKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { Sizing_Pumping_Type_Enum } from "gql/api";
import type { EquipmentTableColumn } from "@/composables/equipment/equipmentTypes";
import {
  calculateCircularFlowVelocityFtSec,
  HPS_DISCHARGE_VELOCITY_LIMIT_FT_SEC
} from "@/libs/calculations/flowVelocity";

const props = defineProps<{
  isAdmin: boolean;
}>();

defineEmits<{
  (event: "select", data: Intakes): void;
}>();

const intakeSearch = useHpsIntakes(props.isAdmin);

const scenarioForm = !props.isAdmin
  ? injectStrict(scenarioFormKey, null)
  : null;
const scenarioCalculations = !props.isAdmin
  ? injectStrict(scenarioCalculationsKey)
  : null;

const isHps = computed(() => {
  if (!scenarioForm) return false;
  return scenarioForm.data.value?.pumping_type === Sizing_Pumping_Type_Enum.Hps;
});

const enableFilter = ref<boolean>(isHps.value);

/**
 * Bottom pump in the string (same ordering as fluid/calc code via pumps_order).
 * Uses live form state so a pump selected in the UI is visible before save.
 */
const pumpHousingOuterDiameterInches = computed(() => {
  if (!scenarioCalculations) return null;
  return (
    scenarioCalculations.bottomPump.value?.r_pump
      ?.housing_outer_diameter_inches ?? null
  );
});

/** True when we have a catalog pump on the bottom xref with a usable housing OD. */
const pumpReadyForSuctionRecommendations = computed(() => {
  const od = pumpHousingOuterDiameterInches.value;
  return od !== null && od !== undefined;
});

const hasLoadedIntakeCatalog = computed(() =>
  Array.isArray(intakeSearch.allRows.value)
);

const filterAndSortActive = computed(
  () => enableFilter.value && isHps.value && !props.isAdmin
);

const flowRateEnteringIntakeBBLD = computed(() => {
  if (!scenarioCalculations) return 0;
  return (
    scenarioCalculations.fluidProperties.fluidPropertiesAtIntake.value
      ?.fluidFlowRateBBLD || 0
  );
});

const flowRateAtDischargeBBLD = computed(() => {
  if (!scenarioCalculations) return 0;
  const context =
    scenarioCalculations.fluidPropertiesAfterPumpDischargeTubingContext.value;
  return context?.flowRatesInSitu?.fluidFlowRateBBLD || 0;
});

function getIntakeVelocityFtSec(intake: Intakes): number | null {
  return calculateCircularFlowVelocityFtSec(
    flowRateEnteringIntakeBBLD.value,
    intake.inner_diameter_inches
  );
}

function getDischargeVelocityFtSec(): number | null {
  return calculateCircularFlowVelocityFtSec(
    flowRateAtDischargeBBLD.value,
    scenarioCalculations?.tubingInternalDiameterAtPumpDischargeIn.value
  );
}

function matchesAnySuctionOuterDiameter(
  intake: Intakes,
  pumpOD: number
): boolean {
  const OD_TOLERANCE = 0.001;
  const candidates = [
    intake.outer_diameter_inches,
    intake.neck_outer_diameter_inches,
    intake.neck_inner_diameter_inches
  ]
    .filter(v => v !== null && v !== undefined)
    .map(v => Number(v));

  return candidates.some(od => Math.abs(od - Number(pumpOD)) < OD_TOLERANCE);
}

const sortedIntakes = computed(() => {
  const rows = intakeSearch.allRows.value;
  if (!rows) {
    return rows;
  }

  // For admin views or non-HPS sizings, keep existing behavior.
  if (props.isAdmin || !isHps.value || !enableFilter.value) {
    return rows;
  }

  // When Filter & Sort is enabled for HPS sizings, enforce:
  // - Primary OD (outer_diameter_inches) must match pump's OD
  // - Suction velocity limit: 9 ft/sec (based on intake ID + intake flow)
  // - Discharge velocity limit: 20 ft/sec (based on discharge tubing ID + discharge flow)

  // Suction OD must match bottom pump housing OD (primary OR secondary OR tertiary)
  const pumpOD = pumpHousingOuterDiameterInches.value;

  if (pumpOD === null || pumpOD === undefined) {
    return [];
  }

  const filteredByPumpOD = rows.filter(row =>
    matchesAnySuctionOuterDiameter(row, pumpOD)
  );

  // If no intakes match the pump OD, return empty array
  if (filteredByPumpOD.length === 0) {
    return [];
  }
  const suctionLimitFtPerSec = 9;
  const dischargeVelocity = getDischargeVelocityFtSec();

  if (
    dischargeVelocity !== null &&
    dischargeVelocity > HPS_DISCHARGE_VELOCITY_LIMIT_FT_SEC
  ) {
    return [];
  }

  const withVelocity = filteredByPumpOD
    .map(row => {
      const velocity = getIntakeVelocityFtSec(row);
      return { row, velocity };
    })
    .filter(entry => entry.velocity !== null);

  const passing = withVelocity.filter(
    entry =>
      (entry.velocity as number) <= suctionLimitFtPerSec &&
      (dischargeVelocity === null ||
        dischargeVelocity <= HPS_DISCHARGE_VELOCITY_LIMIT_FT_SEC)
  );

  const failingIds = new Set(
    withVelocity
      .filter(
        entry =>
          (entry.velocity as number) > suctionLimitFtPerSec ||
          (dischargeVelocity !== null &&
            dischargeVelocity > HPS_DISCHARGE_VELOCITY_LIMIT_FT_SEC)
      )
      .map(entry => entry.row.id)
  );

  // Rows with no calculable velocity are kept but de-prioritized.
  const withoutVelocity = filteredByPumpOD.filter(
    row => !withVelocity.some(entry => entry.row.id === row.id)
  );

  // Sort passing rows by smallest inner diameter first (ID = bore used for velocity), then by increasing velocity.
  passing.sort((a, b) => {
    const idA = a.row.inner_diameter_inches ?? 0;
    const idB = b.row.inner_diameter_inches ?? 0;
    if (idA !== idB) {
      return idA - idB;
    }
    const vA = a.velocity as number;
    const vB = b.velocity as number;
    return vA - vB;
  });

  // Exclude explicit failures from the final list when Filter & Sort is on.
  const passingRows = passing.map(entry => entry.row);
  const remainingWithoutVelocity = withoutVelocity.filter(
    row => !failingIds.has(row.id)
  );

  return [...passingRows, ...remainingWithoutVelocity];
});

const sortedFilteredRows = computed(() => {
  const baseFiltered = intakeSearch.filteredRows.value;
  const sorted = sortedIntakes.value;

  // Preserve undefined while loading so the table can show loading state.
  if (!baseFiltered || !sorted) {
    return sorted as typeof baseFiltered;
  }

  // Always respect the composable's filteredRows (which already apply
  // showDeleted / customer filters and search), then apply our
  // recommendation ordering on top of that.
  const filteredIds = new Set(baseFiltered.map(p => p.id));
  return sorted.filter(intake => filteredIds.has(intake.id));
});

/** Warning: filter on but no bottom pump / no housing OD yet (unsaved selection still works via calcs). */
const showPumpRequiredWarning = computed(
  () =>
    filterAndSortActive.value &&
    hasLoadedIntakeCatalog.value &&
    !pumpReadyForSuctionRecommendations.value
);

/** Warning: pump known but no suctions pass primary-OD + velocity filters. */
const showNoMatchingSuctionsWarning = computed(() => {
  const rows = sortedFilteredRows.value;
  return (
    filterAndSortActive.value &&
    hasLoadedIntakeCatalog.value &&
    pumpReadyForSuctionRecommendations.value &&
    Array.isArray(rows) &&
    rows.length === 0
  );
});

const equipmentWithSortedIntakes = {
  ...intakeSearch,
  columns: computed(() => {
    const columnsRaw = intakeSearch.columns;
    const baseColumns = Array.isArray(columnsRaw)
      ? (columnsRaw as EquipmentTableColumn<Intakes>[])
      : (columnsRaw.value as EquipmentTableColumn<Intakes>[]);

    // Only change the non-admin search/selection view table.
    if (props.isAdmin) {
      return baseColumns;
    }

    const idx = baseColumns.findIndex(c => c.field === "outer_diameter_inches");
    const withoutPrimaryOD = baseColumns.filter(
      c => c.field !== "outer_diameter_inches"
    );

    const flowrateColumn: EquipmentTableColumn<Intakes> = {
      // NOTE: this field is added at runtime for this view only.
      field: "flowrate_ft_sec" as unknown as keyof Intakes,
      header: "Flowrate (ft/s)",
      isNumber: true,
      sortable: true,
      decimalPlaces: 4,
      editable: false,
      format: value => {
        if (value === null || value === undefined) return "";
        const n = Number(value);
        if (Number.isNaN(n)) return "";
        return n.toFixed(4);
      }
    };

    if (idx < 0) {
      return [...withoutPrimaryOD, flowrateColumn];
    }

    return [
      ...withoutPrimaryOD.slice(0, idx),
      flowrateColumn,
      ...withoutPrimaryOD.slice(idx)
    ];
  }),
  allRows: computed(() => {
    // IMPORTANT: don't add view-only computed fields for admin tables;
    // admin edits send row objects back to GraphQL mutations.
    if (props.isAdmin) return intakeSearch.allRows.value;

    const rows = sortedIntakes.value;
    return rows?.map(r => ({
      ...r,
      flowrate_ft_sec: getIntakeVelocityFtSec(r)
    }));
  }),
  filteredRows: computed(() => {
    // IMPORTANT: don't add view-only computed fields for admin tables;
    // admin edits send row objects back to GraphQL mutations.
    if (props.isAdmin) return intakeSearch.filteredRows.value;

    const rows = sortedFilteredRows.value;
    return rows?.map(r => ({
      ...r,
      flowrate_ft_sec: getIntakeVelocityFtSec(r)
    }));
  })
};

provide("equipment", equipmentWithSortedIntakes);
</script>
